import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { WORK_ITEMS } from "@/data/work";
import { CASE_STUDIES } from "@/data/caseStudies";

export default function CaseStudy() {
  const { slug } = useParams();
  const idx = WORK_ITEMS.findIndex((w) => w.id === slug);
  if (idx === -1) return <Navigate to="/work" replace />;

  const item = WORK_ITEMS[idx];
  const cs = CASE_STUDIES[slug];
  const next = WORK_ITEMS[(idx + 1) % WORK_ITEMS.length];

  return (
    <main data-testid={`case-study-${slug}`} className="pt-40 pb-28">
      <Seo
        title={`${item.title} Case Study — Fredline Studios`}
        siteName="Fredline Studios"
        description={cs.tagline}
        image={item.image}
      />

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <Link
            to="/work"
            data-testid="case-study-back-link"
            className="group inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-slate-500 uppercase transition-colors duration-200 hover:text-cyan-300"
          >
            <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-x-1" />
            The Archive
          </Link>
          <span className="mono-label mt-10 block">CASE STUDY /{String(idx + 1).padStart(2, "0")} — {item.category}</span>
          <h1 className="font-display metal-text mt-4 text-6xl font-black leading-[0.9] tracking-tighter sm:text-8xl">
            {item.title}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">{cs.tagline}</p>
        </Reveal>

        <Reveal delay={0.1} className="mt-12 flex flex-wrap gap-x-10 gap-y-3 border-y border-[#1E2335] py-5">
          <span className="font-mono text-[11px] tracking-[0.2em] text-slate-500 uppercase">Year — <span className="text-slate-200">{item.year}</span></span>
          <span className="font-mono text-[11px] tracking-[0.2em] text-slate-500 uppercase">Stack — <span className="text-slate-200">{item.stack.join(" · ")}</span></span>
          <span className="font-mono text-[11px] tracking-[0.2em] text-slate-500 uppercase">Headline result — <span className="text-cyan-300">{item.metric}</span></span>
        </Reveal>

        <Reveal delay={0.15} className="mt-14">
          <div className="chamfer bg-gradient-to-br from-[#2A3045] via-[#1E2335] to-[#2A3045] p-px">
            <div className="chamfer overflow-hidden bg-[#0D0E15]">
              <img
                src={item.image}
                alt={`${item.title} website designed and built by Fredline Studios`}
                className="aspect-[16/9] w-full object-cover"
                data-testid="case-study-hero-image"
              />
            </div>
          </div>
        </Reveal>

        <div className="mt-20 grid gap-12 lg:grid-cols-2">
          <Reveal>
            <span className="mono-label">THE BRIEF</span>
            <p className="mt-4 text-base leading-relaxed text-slate-400">{cs.brief}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="mono-label">THE BUILD</span>
            <p className="mt-4 text-base leading-relaxed text-slate-400">{cs.build}</p>
          </Reveal>
        </div>

        <Reveal className="mt-24">
          <span className="mono-label">PROCESS</span>
          <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-slate-100 sm:text-5xl">
            How it was forged.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-px border border-[#1E2335] bg-[#1E2335] sm:grid-cols-2">
          {cs.process.map((step, i) => (
            <Reveal key={step.title} delay={i * 0.08} className="bg-[#0D0E15]">
              <div
                data-testid={`case-study-process-${i + 1}`}
                className="group h-full p-8 transition-colors duration-300 hover:bg-[#12141F] sm:p-10"
              >
                <span className="font-mono text-sm tracking-[0.3em] text-slate-600 transition-colors duration-300 group-hover:text-cyan-400">
                  /{String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-display mt-5 text-xl font-bold tracking-tight text-slate-100 sm:text-2xl">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-24">
          <span className="mono-label">OUTCOMES</span>
          <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-slate-100 sm:text-5xl">
            Numbers, not adjectives.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-px border border-[#1E2335] bg-[#1E2335] sm:grid-cols-3">
          {cs.results.map((r, i) => (
            <Reveal key={r.label} delay={i * 0.08} className="bg-[#0D0E15]">
              <div data-testid={`case-study-result-${i + 1}`} className="p-10 text-center">
                <p className="font-display metal-text text-5xl font-black tracking-tighter sm:text-6xl">{r.value}</p>
                <p className="mono-label mt-4 text-slate-500">{r.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-24">
          <Link
            to={`/work/${next.id}`}
            data-testid="case-study-next-link"
            className="group flex items-center justify-between gap-6 border border-[#1E2335] bg-[#0D0E15] p-8 transition-colors duration-300 hover:border-cyan-400/40 sm:p-12"
          >
            <div>
              <span className="mono-label text-slate-500">NEXT CASE STUDY</span>
              <p className="font-display mt-2 text-3xl font-extrabold tracking-tight text-slate-100 transition-colors duration-300 group-hover:text-cyan-300 sm:text-5xl">
                {next.title}
              </p>
            </div>
            <ArrowRight className="h-8 w-8 shrink-0 text-slate-600 transition-all duration-300 group-hover:translate-x-2 group-hover:text-cyan-300" />
          </Link>
        </Reveal>

        <Reveal className="mt-16 text-center">
          <span className="mono-label">YOUR PROJECT HERE</span>
          <h2 className="font-display metal-text mx-auto mt-4 max-w-3xl text-3xl font-black leading-[0.95] tracking-tighter sm:text-5xl">
            WANT RESULTS LIKE THESE?
          </h2>
          <Link
            to="/contact"
            data-testid="case-study-cta-button"
            className="mt-10 inline-flex items-center gap-2 bg-cyan-400 px-8 py-4 font-mono text-[12px] font-bold tracking-[0.2em] text-[#07080C] uppercase transition-all duration-200 hover:bg-cyan-300 hover:shadow-[0_0_40px_rgba(0,240,255,0.45)] active:scale-[0.97]"
          >
            Start a Project
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </main>
  );
}
